import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AdvanceFilterComponent } from '../labour-rates-advance-filter/labour-rates-advance-filter.component';
import { LabourRatesFilterForm } from '../../../form-model/labour-rates-filter.form.model';
import { ToastrService } from '../../../services/toastr-service';
import { LabourRatesFilter } from '../../../models/labour-rates-filter.model';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { MasterDataManagementService } from '../../../services/master-data-management.service';
import { ReplaySubject } from 'rxjs';
import { DownloadFilterCriteria, toFilter } from '../../../models/download-filter.model';
import {
  getFilterValue as getSharedFilterValue,
  removeFilter as removeSharedFilter,
  isArrayType as isSharedArrayType,
  buildExpiryDateLabel,
  buildExpiryDateDownloadFilters,
} from '../../../helper/filter-helpers';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { CodeDescription } from '../../../models/code-description.model';
import { AuthPolicies } from '../../../auth/auth-policies';
import { AuthHelper } from '../../../auth/auth-helper';

type FilterValue = string | string[] | boolean | undefined | CodeDescription[];

@Component({
  selector: 'app-labour-rates-filters',
  imports: [ReactiveFormsModule, FormsModule, RouterModule, AdvanceFilterComponent, TagWithOverflowComponent],
  templateUrl: './labour-rates-filters.component.html',
  styleUrls: ['./labour-rates-filters.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LabourRatesFiltersComponent implements AfterViewInit {
  readonly screenWidth = window.innerWidth;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    labourRatesFilterForm: FormGroup<LabourRatesFilterForm>;
  }>;
  showAdvanceFilters: boolean = false;
  savedFilter!: FormGroup<LabourRatesFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @ViewChild(AdvanceFilterComponent) advFilter!: AdvanceFilterComponent;
  isDownload: boolean = false;

  constructor(
    private readonly masterDataManagementService: MasterDataManagementService,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    public readonly authPolicies: AuthPolicies,
  ) {}

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  private subscribeToFormChanges() {
    this.filterFormGroup.controls.labourRatesFilterForm?.controls.applyLabourRatesFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
      }
    });
  }

  updateSelectedFiltersSection() {
    this.markAsTagged('Shop Code', this.filterFormGroup.controls.labourRatesFilterForm.controls.shopCode.value);
    this.markAsTagged('Equipment Type', this.filterFormGroup.controls.labourRatesFilterForm.controls.equipmentType.value);

    const form = this.filterFormGroup.controls.labourRatesFilterForm.controls;
    const expiryLabel = buildExpiryDateLabel(
      form.expiryDateType.value,
      form.expiryDate.value,
      form.expiryDateFrom.value,
      form.expiryDateTo.value,
    );

    if (expiryLabel) {
      this.markAsTagged('Expiry Date', expiryLabel);
    } else {
      this.removeFilter('Expiry Date');
    }
    this.removeFilter('Expiry Type');

    const effectiveDateLabel = buildExpiryDateLabel(
      form.effectiveDateType?.value,
      form.effectiveDate?.value,
      form.effectiveDateFrom?.value,
      form.effectiveDateTo?.value,
    );
    if (effectiveDateLabel) {
      this.markAsTagged('Effective Date', effectiveDateLabel);
    } else {
      this.removeFilter('Effective Date');
    }

    this.updateStatusCountAndTag();
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== false && newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string) {
    return getSharedFilterValue(this.taggedFilters, key);
  }

  isArrayType(value: FilterValue): boolean {
    return isSharedArrayType(value);
  }

  private updateStatusCountAndTag() {
    const filters: LabourRatesFilter = {};
    if (this.filterFormGroup.controls.labourRatesFilterForm.controls.shopCode.value) {
      filters.shopCode = this.filterFormGroup.controls.labourRatesFilterForm.controls.shopCode.value;
    }
    if (this.filterFormGroup.controls.labourRatesFilterForm.controls.equipmentType.value) {
      filters.equipmentType = this.filterFormGroup.controls.labourRatesFilterForm.controls.equipmentType.value;
    }
    const expiryType = this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateType.value;
    if (expiryType === 'BETWEEN') {
      if (this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateFrom.value) {
        filters.expiryDateFrom = this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateFrom.value;
      }
      if (this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateTo.value) {
        filters.expiryDateTo = this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateTo.value;
      }
    } else if (this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDate.value) {
      filters.expiryDate = this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDate.value;
    }
  }

  showAdvanceFiltersModal() {
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
  }

  private saveCurrentFilters(): void {
    this.savedFilter = this.formBuilder.group({
      shopCode: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.labourRatesFilterForm.controls.shopCode.value),
      equipmentType: this.formBuilder.control<CodeDescription[] | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.equipmentType.value,
      ),
      expiryDateType: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateType.value,
      ),
      expiryDateFrom: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateFrom.value,
      ),
      expiryDateTo: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateTo.value,
      ),
      expiryDate: this.formBuilder.control<string | null>(this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDate.value),
      effectiveDateType: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDateType?.value,
      ),
      effectiveDateFrom: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDateFrom?.value,
      ),
      effectiveDateTo: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDateTo?.value,
      ),
      effectiveDate: this.formBuilder.control<string | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDate?.value,
      ),
      applyLabourRatesFilter: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.value,
      ),
    });
  }

  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.labourRatesFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  closeAdvance() {
    this.updateSelectedFiltersSection();
    this.showAdvanceFilters = false;
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    if (taggedFilter.key === 'Expiry Date' || taggedFilter.key === 'Effective Date') {
      return taggedFilter.value as string;
    }
    if (taggedFilter.key === 'Equipment Type' && Array.isArray(taggedFilter.value) && itemNumber >= 0) {
      return (taggedFilter.value as CodeDescription[])[itemNumber].code.toUpperCase();
    }
    return '';
  }

  getTaggedShopCodes(): string[] {
    const shopCode = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Shop Code');
    return shopCode && Array.isArray(shopCode.value) ? (shopCode.value as string[]) : [];
  }

  getTaggedEquipmentType(): CodeDescription[] {
    const equipmentType = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Equipment Type');
    return equipmentType && Array.isArray(equipmentType.value) ? (equipmentType.value as CodeDescription[]) : [];
  }

  getTaggedExpiryDate(): string {
    const expiryDate = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Expiry Date');
    return expiryDate && typeof expiryDate.value === 'string' ? expiryDate.value : '';
  }

  unselectShopCode() {
    return (shopCode: string) => {
      let key = this.taggedFilters.find((x) => x.key == 'Shop Code');
      const shopCodeControl = this.filterFormGroup.controls.labourRatesFilterForm.controls.shopCode;
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((scode) => scode != shopCode);

      shopCodeControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1, itemValue = ''): void {
    switch (taggedFilter.key) {
      case 'Shop':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let shopCode = taggedFilter.value as string[];
          const updatedShopCodes: string[] = shopCode.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.labourRatesFilterForm.controls.shopCode.setValue(updatedShopCodes.length ? updatedShopCodes : null);
        }
        break;

      case 'Equipment Type':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let equipmentType = taggedFilter.value as CodeDescription[];
          const updatedEquipmentTypes: CodeDescription[] = equipmentType.filter(
            (_: CodeDescription, index: number) => index !== itemNumber,
          );
          this.filterFormGroup.controls.labourRatesFilterForm.controls.equipmentType.setValue(
            updatedEquipmentTypes.length ? updatedEquipmentTypes : null,
          );
        }
        break;

      case 'Expiry Date':
        // Clear all expiry date fields
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateType.setValue('equals');
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDate.setValue(null);
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateFrom.setValue(null);
        this.filterFormGroup.controls.labourRatesFilterForm.controls.expiryDateTo.setValue(null);
        break;

      case 'Effective Date':
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDateType?.setValue(null);
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDate?.setValue(null);
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDateFrom?.setValue(null);
        this.filterFormGroup.controls.labourRatesFilterForm.controls.effectiveDateTo?.setValue(null);
        break;

      default:
        break;
    }
    this.filterFormGroup.controls.labourRatesFilterForm.controls.applyLabourRatesFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  onDownload() {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.masterDataManagementService.downloadLabourRatesToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('shop-labour-rates'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.labourRatesFilterForm.controls;

    if (this.authPolicies.isShopUser) {
      const assignedShops = (AuthHelper.user?.maintenanceShops ?? []).map((s) => s.maintenanceShopCode);
      if (assignedShops.length > 0) {
        filters.push(toFilter('shop_code', assignedShops));
      }
    } else {
      const shopCodes = form.shopCode?.value;
      if (shopCodes && shopCodes.length > 0) {
        filters.push(toFilter('shop_code', shopCodes));
      }
    }

    const equipmentTypes = form.equipmentType?.value;
    if (equipmentTypes && equipmentTypes.length > 0) {
      filters.push(
        toFilter(
          'equipment_type',
          equipmentTypes.map((et) => et.code.toUpperCase()),
        ),
      );
    }

    filters.push(...this.buildExpiryDateFilters());

    return filters;
  }

  private buildExpiryDateFilters(): DownloadFilterCriteria[] {
    const form = this.filterFormGroup.controls.labourRatesFilterForm.controls;
    return buildExpiryDateDownloadFilters(
      'expiry_date',
      form.expiryDateType?.value,
      form.expiryDate?.value,
      form.expiryDateFrom?.value,
      form.expiryDateTo?.value,
    );
  }
}
